import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  InputLabel, MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent, TextField,
  Typography
} from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Modal } from '../../../../customComponents/Modal'
import { AttachMoney } from '@mui/icons-material'
import {
  AddStartTrainingAttendancePlan,
  GetTrainings,
  UpdateStartTrainingAttendancePlan
} from '../../../../../services/AnnualPlanService'
import { EventDisplay } from '../../../../customComponents/EventDisplay'
import { ActivityEditSession } from '../ActivityEditSession'
import { TrainingType, type Training } from '../../../../../models/Training'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { budgetNeedsReview } from '../../../../../utils/budgetUtil'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

export interface AddStartTrainingAttendancePlanFormValues {
  priorityId: number
  startTrainingId: number | null
  numberOfAttendees: number | null
  budgetRequest: string
  isImplementationPhase: boolean
}

export interface UpdateStartTrainingAttendancePlanFormValues extends AddStartTrainingAttendancePlanFormValues {
  id: number
  isUpdate: boolean
}

interface Props {
  open: boolean
  annualPlanId: number
  rcnId: number
  priorityId: number
  initialValues?: UpdateStartTrainingAttendancePlanFormValues
  onClose: () => void
  onSaved: () => void
  isImplementationPhase: boolean
  usedIds: number[]
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const StartTrainingAttendancePlanModal = (props: Props): JSX.Element => {
  const isEditing = props?.initialValues != null
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [formValues, setFormValues] = useState<AddStartTrainingAttendancePlanFormValues>({
    priorityId: props.priorityId ?? 0,
    startTrainingId: null,
    numberOfAttendees: null,
    budgetRequest: '',
    isImplementationPhase: props.isImplementationPhase
  })
  const [submitted, setIsSubmitted] = useState(false)
  const [startTrainingOptions, setStartTrainingOptions] = useState<Training[]>([])

  useEffect(() => {
    const fetchStartTrainingOptions = async (): Promise<void> => {
      const annualPlanTrainings = await handleApiResponse(GetTrainings(TrainingType.START))
      if (annualPlanTrainings == null) return
      // Filter out trainings that are already used in other attendance plans, unless it's the one being edited
      const filteredTrainings = annualPlanTrainings.filter(t => !props.usedIds.includes(t.id ?? -1) || (props.initialValues != null && t.id === props.initialValues.startTrainingId))
      setStartTrainingOptions(filteredTrainings)
    }

    void fetchStartTrainingOptions()
  }, [])

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues(props.initialValues)
    }
  }, [props.initialValues])

  const hasInvalidFormValues = formValues.startTrainingId == null ||
    formValues.numberOfAttendees == null ||
    formValues.budgetRequest == null

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.initialValues != null) {
      if (budgetNeedsReview(props.initialValues.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }
    await continueSubmit()
  }

  const continueSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateStartTrainingAttendancePlan({ ...formValues, id: props.initialValues.id, isUpdate: props.initialValues.isUpdate })
      : await AddStartTrainingAttendancePlan(formValues)

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success(`Attendance Plan ${isEditing ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  const selectedTraining = startTrainingOptions.find((training) => training?.id != null && training.id.toString() === formValues.startTrainingId?.toString())

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        title={`${isEditing ? 'Update' : 'Add'} START Training Attendance Plan`}
        open={props.open}
        data-testid={isEditing ? 'editStartTrainingAttendancePlanModal' : 'addStartTrainingAttendancePlanModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {
              props.isImplementationPhase && props.initialValues == null &&
                <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                  backgroundColor: 'var(--blue-500)',
                  color: 'white',
                  fill: 'white'
                }} severity="info"
                >The addition of this item will prompt a review from the START team.
                </Alert>
            }
            <Typography variant='h5'>START Trainings and Training Series</Typography>
            <Typography variant='body1'>Please select the START training or training series that will be attended, enter the number of attendees along with the budget request.</Typography>
            <FormControl
              fullWidth
              required={formValues.startTrainingId == null}
              error={submitted && formValues.startTrainingId == null}
            >
              <InputLabel id='training-label'>Training Name</InputLabel>
              <Select
                disabled={isEditing}
                labelId='training-label'
                id='training'
                value={startTrainingOptions.length > 0 ? formValues.startTrainingId ?? '' : ''}
                label='Training Name'
                name='startTrainingId'
                data-testid='training'
                onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.startTrainingId>) => void}
              >
                {startTrainingOptions.length === 0 && <Typography variant='body1' textAlign='center'>No trainings available.</Typography>}
                {startTrainingOptions.map((training) => (
                  <MenuItem key={training.id} value={training.id} data-testid='trainingOption'>{training.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTraining != null &&
              <EventDisplay
                eventBoxStyle={{ width: '2.25em', height: '3.375em', my: '.5em' }}
                event={{ title: selectedTraining.title, date: selectedTraining.meetingDate }}
                description={selectedTraining.description}
                seriesLetter='S'
              />
            }

            <TextField
              name='numberOfAttendees'
              label='Number Attending'
              value={formValues.numberOfAttendees ?? ''}
              data-testid='numberOfAttendees'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValues.numberOfAttendees == null}
              error={submitted && formValues.numberOfAttendees == null}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography variant='body1'>Does this require additional funding to complete?</Typography>

              <FormControl sx={{ width: 'fit-content' }} variant="outlined">
                <InputLabel htmlFor="budget-request">Budget Request</InputLabel>
                <OutlinedInput
                  id="budget-request"
                  startAdornment={
                    <InputAdornment position="start">
                      <AttachMoney />
                    </InputAdornment>
                  }
                  name='budgetRequest'
                  label="Budget Request"
                  value={formValues.budgetRequest}
                  data-testid='budgetRequest'
                  onChange={handleFieldUpdate}
                  required={formValues.budgetRequest === ''}
                  error={submitted && formValues.budgetRequest === ''}
                />
              </FormControl>
            </Box>

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues.id}
                activityName='START Training Attendance Plan'
                onClose={props.onClose}
              />
            }
          </Box>
        }
    />
  )
}
